import React, { useEffect, useState } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { useWidgetsStore } from '../../stores/widgetsStore';

const Widgets = () => {
  const { accessToken } = useAuthStore();
  const { widgets, widgetLayouts, fetchWidgets, updateWidget, isLoading, error } = useWidgetsStore();
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});

  useEffect(() => {
    if (accessToken) {
      fetchWidgets(accessToken);
    }
  }, [accessToken, fetchWidgets]);

  const handleStartEdit = (widget) => {
    setEditingId(widget.id);
    setEditValues({
      label: widget.label,
      description: widget.description || '',
      maximizable_default: widget.maximizable_default === 1,
      default_widget_layout_id: widget.default_widget_layout_id || '',
    });
  };

  const handleSaveEdit = async (widgetId) => {
    const result = await updateWidget(widgetId, editValues, accessToken);
    if (result.success) {
      setEditingId(null);
      setEditValues({});
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValues({});
  };

  const handleToggleActive = async (widget) => {
    const result = await updateWidget(widget.id, { is_active: !widget.is_active }, accessToken);
    if (result.success) {
      await fetchWidgets(accessToken);
    }
  };

  if (isLoading && widgets.length === 0) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error && widgets.length === 0) {
    return (
      <div className="surface rounded-lg p-6">
        <p className="text-red-600 dark:text-red-400">{error}</p>
      </div>
    );
  }

  return (
    <div className="surface rounded-lg shadow-lg p-6">
      <h2 className="text-2xl font-bold text-primary mb-6">CMS Widgets</h2>

      {/* Widgets List */}
      <div className="space-y-4">
        {widgets.map((widget) => (
          <div
            key={widget.id}
            className="surface border border-gray-200 dark:border-gray-700 rounded-lg p-4"
          >
            {editingId === widget.id ? (
              // Edit Mode
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">
                    Label
                  </label>
                  <input
                    type="text"
                    value={editValues.label}
                    onChange={(e) => setEditValues({ ...editValues, label: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-primary"
                    maxLength={160}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">
                    Description
                  </label>
                  <textarea
                    value={editValues.description}
                    onChange={(e) => setEditValues({ ...editValues, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">
                    Default Widget Layout
                  </label>
                  <select
                    value={editValues.default_widget_layout_id}
                    onChange={(e) => setEditValues({ ...editValues, default_widget_layout_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-primary"
                  >
                    <option value="">None</option>
                    {widgetLayouts.map((layout) => (
                      <option key={layout.id} value={layout.id}>
                        {layout.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id={`maximizable-${widget.id}`}
                    checked={editValues.maximizable_default}
                    onChange={(e) => setEditValues({ ...editValues, maximizable_default: e.target.checked })}
                    className="mr-2"
                  />
                  <label htmlFor={`maximizable-${widget.id}`} className="text-sm text-secondary">
                    Maximizable by default
                  </label>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleSaveEdit(widget.id)}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    Save
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded text-secondary hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              // View Mode
              <div>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-semibold text-primary">{widget.label}</h3>
                      <span className="px-2 py-1 text-xs font-mono bg-gray-100 dark:bg-gray-700 text-secondary rounded">
                        {widget.code}
                      </span>
                      {widget.maximizable_default === 1 && (
                        <span className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded">
                          Maximizable
                        </span>
                      )}
                      {widget.default_widget_layout_id && (
                        <span className="px-2 py-1 text-xs bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 rounded">
                          Layout: {widgetLayouts.find(l => l.id === widget.default_widget_layout_id)?.label || widget.default_widget_layout_id}
                        </span>
                      )}
                    </div>
                    {widget.description && (
                      <p className="text-sm text-secondary mt-1">{widget.description}</p>
                    )}
                  </div>

                  <div className="flex items-center gap-3 ml-4">
                    {/* Active Toggle */}
                    <button
                      onClick={() => handleToggleActive(widget)}
                      className={`flex-shrink-0 relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        widget.is_active
                          ? 'bg-primary-600'
                          : 'bg-gray-200 dark:bg-gray-700'
                      }`}
                      title={widget.is_active ? 'Active' : 'Inactive'}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          widget.is_active ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>

                    {/* Edit Button */}
                    <button
                      onClick={() => handleStartEdit(widget)}
                      className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                      title="Edit"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {widgets.length === 0 && !isLoading && (
        <p className="text-center text-secondary py-8">No widgets found</p>
      )}
    </div>
  );
};

export default Widgets;
